/**
 * An agent's own profile — Step 174.
 *
 * Kai: *"agents should be able to click their profile."* There was nowhere to
 * go: the avatar in the top bar was decoration, and the one thing an agent
 * most wants to set about themselves — how their replies are signed — had no
 * home at all.
 *
 * Deliberately small. Identity here comes from the directory (Entra syncs
 * name, job title, department), so this is not an edit-your-details page and
 * pretending otherwise would invite changes that the next sync overwrites.
 * What it owns is the one thing that is genuinely theirs and nobody else's.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PenLine, ShieldCheck } from 'lucide-react';
import { apiFetch, apiSend } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useDirty } from '../lib/dirty';
import { useDocumentTitle } from '../lib/useBranding';
import { qk } from '../lib/queryClient';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RichTextEditor } from '../components/ui/richtext';
import { Skeleton } from '../components/ui/skeleton';

export function ProfilePage() {
  useDocumentTitle('My profile');
  const { me } = useAuth();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: qk.mySignature,
    queryFn: () =>
      apiFetch<{
        signature: string | null;
        generated: string;
      }>('/api/org/me/signature'),
  });

  const [draft, setDraft] = useState<string | null>(null);
  useEffect(() => {
    if (!q.data) return;
    if (draft === null) setDraft(q.data.signature ?? '');
  }, [q.data, draft]);

  const dirty = draft !== null && draft !== (q.data?.signature ?? '');
  const clearDirty = useDirty(dirty);

  const save = useMutation({
    mutationFn: () => apiSend('/api/org/me/signature', 'PUT', { signature: draft?.trim() ? draft : null }),
    onSuccess: (res) => {
      if (!res.ok) return toast.error(res.error ?? 'That could not be saved.');
      clearDirty();
      void qc.invalidateQueries({ queryKey: qk.mySignature });
      toast.success('Saved.');
    },
  });

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <h1 className="mb-1 text-lg font-semibold">My profile</h1>
      <p className="mb-5 text-sm text-muted-foreground">
        {me?.user.name ?? me?.user.email}
        {me?.user.email && me?.user.name ? ` · ${me.user.email}` : ''}
      </p>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <PenLine className="h-4 w-4" /> Email signature
          </CardTitle>
          <CardDescription>
            Added to the bottom of every reply you send by email. It is not shown on the ticket itself — the
            thread already says who wrote each message.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {q.isPending && <Skeleton className="h-40 w-full" />}
          {q.isError && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm">
              <p className="text-destructive">That could not be loaded.</p>
              <Button variant="outline" size="sm" className="mt-2" onClick={() => void q.refetch()}>
                Retry
              </Button>
            </div>
          )}
          {q.data && draft !== null && (
            <>
              <RichTextEditor
                value={draft}
                onChange={setDraft}
                placeholder="Your name, role, anything else worth saying"
              />
              {/* The generated one, shown so somebody can see what their
                  replies ALREADY look like before deciding to change it — and
                  so that clearing the box reads as "back to this" rather than
                  "send nothing". */}
              <div className="rounded-md border border-dashed p-3">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {draft.trim() ? 'Leave it empty and this is used instead' : 'Currently used'}
                </p>
                <div
                  className="text-sm [&_*]:!text-foreground"
                  // Generated on the server from your name, job title and the
                  // desk's name — no user input reaches it.
                  dangerouslySetInnerHTML={{ __html: q.data.generated || '<p>Nothing — add one above.</p>' }}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="mt-4 flex items-center gap-2">
        <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save changes'}
        </Button>
        {dirty && (
          <Button variant="ghost" onClick={() => setDraft(q.data?.signature ?? '')}>
            Discard
          </Button>
        )}
      </div>
    </div>
  );
}
